/**
 * ops 依頼の添付ファイル（append-only の証跡）の共有定義。notes と同じく **1 添付 1 ドキュメント**の別
 * Firestore コレクションに積み、実体（バイト列）は GCS に保存する（Firestore にはメタのみ）。コレクション名の
 * 導出規則・添付メタ型・サイズ/件数上限・許可判定・表示ヘルパーを product-api（書き込み・配信）・web（入力検証・
 * 表示）・customer-success MCP（一覧）が同じ source で共有し drift を防ぐ。env 判定は各アプリが行い isProduction だけを渡す（ops-core は
 * env を読まない・純粋関数に保つ）。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼の添付ファイル（append-only 証跡。GCS 実体 + Firestore メタ）の共有型・コレクション名規則・サイズ/件数上限・許可判定・表示ヘルパー。product-api（書込・配信）・web（検証・表示）・customer-success MCP（一覧）が同一 source を参照し drift を防ぐ
 * @graph-connects none
 */
/** 添付メタ（Firestore）コレクションの本番名（dev は `_dev` を付す）。 @graph-connects none */
export const OPS_ATTACHMENTS_COLLECTION = 'ops_request_attachments';
/** 1 ファイルあたりの最大バイト数（25 MiB）。Cloud Run のリクエスト上限（~32MB）内に収める。 @graph-connects none */
export const OPS_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** 1 依頼あたりの添付ファイル最大数。 @graph-connects none */
export const OPS_ATTACHMENT_MAX_COUNT = 10;
/**
 * 実行ファイル・スクリプト等の危険な content-type（拒否リスト）。「任意のファイル」を許容しつつ実行系だけを弾く。
 * 非画像は配信時に `Content-Disposition: attachment` + `nosniff` で無害化するため、拒否は実行系に限定する。
 * @graph-connects none
 */
export const OPS_ATTACHMENT_BLOCKED_MIME = [
    'application/x-msdownload',
    'application/x-msdos-program',
    'application/x-executable',
    'application/vnd.microsoft.portable-executable',
    'application/x-sh',
    'application/x-shellscript',
    'application/x-bat',
    'application/x-mach-binary',
    'application/x-elf',
    'application/java-archive',
];
/**
 * 添付メタの Firestore コレクション名を返す。本番は base 名（`ops_request_attachments`）、それ以外は `_dev` 隔離。
 * @graph-connects none
 */
export function opsAttachmentsCollectionName(isProduction) {
    return isProduction ? OPS_ATTACHMENTS_COLLECTION : `${OPS_ATTACHMENTS_COLLECTION}_dev`;
}
/** content-type を正規化する（小文字・パラメータ（`; charset=...`）除去・前後空白除去）。 @graph-connects none */
function normalizeMime(contentType) {
    return contentType.split(';')[0]?.trim().toLowerCase() ?? '';
}
/**
 * この content-type を添付として許可してよいか（実行系拒否リストに無ければ許可）。「任意のファイル」方針。
 * @graph-connects none
 */
export function isOpsAttachmentMimeAllowed(contentType) {
    const mime = normalizeMime(contentType);
    if (mime === '')
        return false;
    return !OPS_ATTACHMENT_BLOCKED_MIME.includes(mime);
}
/**
 * ブラウザに inline 表示してよい画像か（ラスタ画像のみ true）。SVG は script を含み得るため inline 不可＝
 * ダウンロード扱い（XSS 防御）。配信側の `Content-Disposition` 判定に使う。
 * @graph-connects none
 */
export function isInlineImageMime(contentType) {
    const mime = normalizeMime(contentType);
    return (mime === 'image/jpeg' || mime === 'image/png' || mime === 'image/gif' || mime === 'image/webp');
}
/** バイト数を人間可読なサイズ文字列に整形する（B / KB / MB、小数第1位）。 @graph-connects none */
export function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${String(bytes)} B`;
    const kb = bytes / 1024;
    if (kb < 1024)
        return `${(Math.round(kb * 10) / 10).toString()} KB`;
    const mb = kb / 1024;
    return `${(Math.round(mb * 10) / 10).toString()} MB`;
}
//# sourceMappingURL=attachments.js.map